import React, { useMemo } from 'react';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import FeaturedProgramCard from './FeaturedProgramCard';
import { useRevealOnScroll } from '../../hooks/useRevealOnScroll';
import type { ProjectCardProject } from './ProjectCard';

interface FeaturedProgramsSectionProps {
  id?: string;
  projects: ProjectCardProject[];
  onProjectClick: (project: ProjectCardProject) => void;
  onBrowseAll?: () => void;
}

const FeaturedProgramsSection: React.FC<FeaturedProgramsSectionProps> = ({
  id,
  projects,
  onProjectClick,
  onBrowseAll,
}) => {
  const { ref: headerRef, visible: headerVisible } = useRevealOnScroll<HTMLElement>();
  const { ref: gridRef, visible: gridVisible } = useRevealOnScroll<HTMLDivElement>();

  const featuredProjects = useMemo(() => projects.slice(0, 3), [projects]);

  return (
    <section
      {...(id ? { id } : {})}
      className={`featured-programs-section content-auto relative overflow-hidden bg-brand-graphite py-10 text-white md:py-14 lg:py-16 ${
        id ? 'scroll-mt-28 md:scroll-mt-32' : ''
      }`}
    >
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-5 md:px-6">
        <header
          ref={headerRef}
          className={`reveal-up mb-8 flex flex-col gap-6 md:mb-10 lg:mb-12 lg:flex-row lg:items-end lg:justify-between lg:gap-10 ${
            headerVisible ? 'reveal-up--visible' : ''
          }`}
        >
          <div className="max-w-2xl">
            <p className="eyebrow-accent mb-3 tracking-[0.18em]">03 • NOS RÉALISATIONS</p>
            <div
              className={`accent-dash reveal-line ${headerVisible ? 'reveal-line--visible' : ''}`}
              aria-hidden
            />
            <h2 className="headline-brand-section mt-6 max-w-3xl text-white">
              Des projets{' '}
              <span className="headline-brand-accent">qui nous ressemblent,</span>{' '}
              des lieux qui vous ressemblent.
            </h2>
            <p className="mt-4 max-w-xl text-[15px] font-normal leading-[1.65] text-white/65 sm:mt-5 sm:text-base lg:text-[17px]">
              Découvrez une sélection de nos réalisations.
            </p>
          </div>

          {onBrowseAll ? (
            <button
              type="button"
              onClick={onBrowseAll}
              className="inline-flex w-fit shrink-0 items-center gap-2 self-start rounded-md border border-orange-500 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-500 transition-[background-color,color,transform] duration-normal hover:bg-orange-500/10 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/50 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-graphite lg:self-end"
            >
              Voir tous les projets
              <ArrowForwardIcon className="h-4 w-4" aria-hidden />
            </button>
          ) : null}
        </header>

        <div
          ref={gridRef}
          className={`reveal-stagger grid grid-cols-1 items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8 ${
            gridVisible ? 'reveal-stagger--active' : ''
          }`}
        >
          {featuredProjects.map((project) => (
            <div key={project.id} className="featured-program-card__slot h-full min-h-0">
              <FeaturedProgramCard
                project={project}
                onClick={() => onProjectClick(project)}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProgramsSection;
